import { useConfirmation } from '../../contexts/ConfirmationContext';
import { useNavigationSafety } from '../../contexts/NavigationSafetyContext';
import toast from 'react-hot-toast';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { 
  HomeIcon, 
  DocumentTextIcon, 
  PlusIcon, 
  CogIcon, 
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  MusicalNoteIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
  StarIcon,
  VideoCameraIcon,
  WrenchScrewdriverIcon,
  DocumentDuplicateIcon,
  PhotoIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

export const Navigation: React.FC = () => {
  const { user, hasRole, logout } = useAuth();
  const location = useLocation();
  const confirm = useConfirmation();
  const safety = useNavigationSafety();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // TypeScript Interfaces für Navigation
  interface NavigationSubItem {
    name: string;
    href: string;
    icon: any;
    roles?: string[];
    disabled?: boolean;
  }

  interface NavigationItemSingle {
    name: string;
    href: string;
    icon: any;
    type: 'single';
  }

  interface NavigationItemDropdown {
    name: string;
    icon: any;
    type: 'dropdown';
    items: NavigationSubItem[];
  }

  type NavigationItem = NavigationItemSingle | NavigationItemDropdown;

  // Navigation-Struktur mit Dropdown-Menüs
  const navigationItems: NavigationItem[] = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      type: 'single'
    },
    {
      name: 'Artikel',
      icon: DocumentTextIcon,
      type: 'dropdown',
      items: [
        { name: 'Alle Artikel', href: '/all-articles', icon: ClipboardDocumentListIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Meine Artikel', href: '/articles', icon: DocumentTextIcon, roles: ['autor', 'redakteur', 'administrator'] },
        { name: 'Neuer Artikel', href: '/wizard', icon: PlusIcon, roles: ['autor', 'redakteur', 'administrator'] }
      ]
    },
    {
      name: 'Bücher',
      icon: BookOpenIcon,
      type: 'dropdown',
      items: [
        { name: 'Bücherliste', href: '/books', icon: BookOpenIcon, roles: ['autor', 'redakteur', 'administrator'] },
        { name: 'Rezensionen', href: '/reviews', icon: StarIcon, roles: ['administrator', 'redakteur'] }
      ]
    },
    {
      name: 'Audio/Video',
      icon: MusicalNoteIcon,
      type: 'dropdown',
      items: [
        { name: 'Audio', href: '/audio', icon: MusicalNoteIcon, roles: ['autor', 'redakteur', 'administrator'], disabled: true },
        { name: 'Video', href: '/video', icon: VideoCameraIcon, roles: ['autor', 'redakteur', 'administrator'], disabled: true }
      ]
    },
    {
      name: 'Tools',
      icon: WrenchScrewdriverIcon,
      type: 'dropdown',
      items: [
        { name: 'KI-Autoren', href: '/ki-autoren', icon: WrenchScrewdriverIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Newsletter', href: '/newsletter', icon: EnvelopeIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Dachthekenduett', href: '/dachthekenduett', icon: DocumentDuplicateIcon, roles: ['administrator', 'redakteur'] },
        { name: 'InDesign zu ef-magazin.de', href: '/artikel-extraktor', icon: DocumentDuplicateIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Einblendung Jetzt!', href: '/einblendung-jetzt', icon: VideoCameraIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Social-Media', href: '/social-media', icon: PhotoIcon, roles: ['administrator', 'redakteur'] },
        { name: 'Umbruchentferner', href: '/umbruchentferner', icon: DocumentDuplicateIcon, roles: ['administrator', 'redakteur'] }
      ]
    }
  ];

  // Filtere Navigation basierend auf Benutzer-Rollen
  const getFilteredNavigation = (): NavigationItem[] => {
    return navigationItems
      .map(item => {
        if (item.type === 'single') {
          return item;
        } else {
          return {
            ...item,
            type: 'dropdown' as const,
            items: item.items.filter(subItem => 
              !subItem.roles || subItem.roles.some(role => hasRole([role]))
            )
          };
        }
      })
      .filter(item => {
        // Entferne Dropdown-Menüs, die keine sichtbaren Einträge haben
        if (item.type === 'dropdown') {
          return item.items.length > 0;
        }
        return true;
      });
  };

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const isParentActive = (items: NavigationSubItem[]) => {
    return items.some(item => isActive(item.href));
  };

  const handleDropdownHover = (itemName: string) => {
    setOpenDropdown(itemName);
  };

  const handleDropdownLeave = (event: React.MouseEvent) => {
    // Prüfe ob die Maus zu einem verwandten Element (Dropdown) bewegt wird
    const relatedTarget = event.relatedTarget as HTMLElement;
    
    // Prüfe ob relatedTarget ein Element ist UND die closest()-Methode hat
    if (relatedTarget && typeof relatedTarget.closest === 'function' && (
      relatedTarget.closest('[data-dropdown]') || 
      relatedTarget.closest('[data-dropdown-parent]')
    )) {
      return; // Nicht schließen wenn zur Dropdown oder Parent bewegt
    }
    setOpenDropdown(null);
  };

  const handleLogout = async () => {
    if (safety?.busy) { toast.error('Bitte warte, bis der laufende Vorgang abgeschlossen ist.'); return; }
    if (safety?.dirty && !(await confirm({ title: 'Abmelden und Änderungen verwerfen?', description: 'Noch nicht gespeicherte oder übernommene Eingaben gehen beim Abmelden verloren.', confirmLabel: 'Abmelden', danger: true }))) return;
    try { await logout(); } catch { toast.error('Abmelden fehlgeschlagen. Bitte versuche es erneut.'); }
  };

  const handleUserMenuClick = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleUserMenuClose = () => {
    setIsUserMenuOpen(false);
  };

  // Click outside handler für das User-Menü
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav aria-label="Hauptnavigation" className="bg-white shadow-sm border-b border-gray-200 fixed top-2 left-2 right-2 z-50 rounded-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Mobile menu button - Links */}
          <div className="flex items-center lg:hidden mr-4">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="sr-only">Navigation öffnen</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Logo und Desktop Navigation */}
          <div className="flex flex-1">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-blue-600">
                e-Redaktion.org
              </Link>
            </div>
            
            {/* Desktop Navigation Links */}
            <div className="hidden lg:ml-6 lg:flex lg:gap-4 lg:flex-1 lg:justify-center lg:items-center">
              {getFilteredNavigation().map((item) => {
                if (item.type === 'single') {
                  return (
                    <div key={item.name} className="relative">
                      <Link
                        to={item.href}
                        className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium ${
                          isActive(item.href)
                            ? 'border-blue-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.name}
                      </Link>
                    </div>
                  );
                } else {
                  return (
                    <div key={item.name} className="relative">
                      <div data-dropdown-parent onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpenDropdown(null); }}
                        onKeyDown={event => { if (event.key === 'Escape') { setOpenDropdown(null); event.currentTarget.querySelector('button')?.focus(); } }}>
                      <button type="button" aria-expanded={openDropdown === item.name} aria-controls={`nav-${item.name}`}
                        onClick={() => setOpenDropdown(openDropdown === item.name ? null : item.name)}
                        className={`inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium cursor-pointer ${
                          isParentActive(item.items)
                            ? 'border-blue-500 text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                        onMouseEnter={() => handleDropdownHover(item.name)}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.name}
                        <ChevronDownIcon className="w-3 h-3 ml-1" />
                        
                      </button>
                        {/* Dropdown Menu */}
                        {openDropdown === item.name && (
                          <div
                            data-dropdown
                            id={`nav-${item.name}`}
                            className="absolute top-full left-0 mt-0 w-max min-w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
                            onMouseEnter={() => handleDropdownHover(item.name)}
                            onMouseLeave={handleDropdownLeave}
                          >
                            {item.items.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.disabled ? '#' : subItem.href}
                                className={`block px-4 py-2 text-sm ${
                                  subItem.disabled
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : isActive(subItem.href)
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                onClick={subItem.disabled ? (e) => e.preventDefault() : () => setOpenDropdown(null)}
                                aria-current={isActive(subItem.href) ? 'page' : undefined}
                                aria-disabled={subItem.disabled || undefined}
                              >
                                <div className="flex items-center whitespace-nowrap">
                                  <subItem.icon className="w-4 h-4 mr-2 shrink-0" />
                                  {subItem.name}
                                  {subItem.disabled && (
                                    <span className="ml-auto text-xs text-gray-400">(Bald)</span>
                                  )}
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>

          {/* Benutzer-Menü */}
          <div className="hidden lg:ml-6 lg:flex lg:items-center">
            <div className="relative" ref={userMenuRef} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsUserMenuOpen(false); }} onKeyDown={event => { if (event.key === 'Escape') { setIsUserMenuOpen(false); event.currentTarget.querySelector('button')?.focus(); } }}>
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUserMenuClick}
                  aria-expanded={isUserMenuOpen}
                  className="flex items-center"
                >
                  <UserIcon className="w-4 h-4 mr-2" />
                  {user?.name}
                  <ChevronDownIcon className="w-4 h-4 ml-2" />
                </Button>
              </div>
              
              {/* Dropdown-Menü */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={handleUserMenuClose}
                  >
                    <div className="flex items-center">
                      <Cog6ToothIcon className="w-4 h-4 mr-2" />
                      Einstellungen
                    </div>
                  </Link>
                  {hasRole(['administrator', 'redakteur']) && (
                    <Link
                      to="/admin"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={handleUserMenuClose}
                    >
                      <div className="flex items-center">
                        <CogIcon className="w-4 h-4 mr-2" />
                        Administration
                      </div>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleUserMenuClose();
                      handleLogout();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      <UserIcon className="w-4 h-4 mr-2" />
                      Abmelden
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div id="mobile-menu" className="lg:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto" onKeyDown={event => { if (event.key === 'Escape') { setIsMobileMenuOpen(false); document.querySelector<HTMLButtonElement>('button[aria-controls="mobile-menu"]')?.focus(); } }}>
          <div className="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
            {getFilteredNavigation().map((item) => {
              if (item.type === 'single') {
                return (
                  <div key={item.name}>
                    <Link
                      to={item.href}
                      className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                        isActive(item.href)
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </div>
                    </Link>
                  </div>
                );
              } else {
                return (
                  <div key={item.name}>
                    <div className="block pl-3 pr-4 py-2 border-l-4 text-base font-medium border-transparent text-gray-600">
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </div>
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.disabled ? '#' : subItem.href}
                          className={`block pl-3 pr-4 py-2 text-sm font-medium ${
                            subItem.disabled
                              ? 'text-gray-400 cursor-not-allowed'
                              : isActive(subItem.href)
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                          }`}
                          onClick={subItem.disabled ? (e) => e.preventDefault() : () => setIsMobileMenuOpen(false)}
                        >
                          <div className="flex items-center whitespace-nowrap">
                            <subItem.icon className="w-4 h-4 mr-3 shrink-0" />
                            {subItem.name}
                            {subItem.disabled && (
                              <span className="ml-auto text-xs text-gray-400">(Bald)</span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
            })}
          </div>
          
          {/* Mobile user menu */}
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <UserIcon className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">{user?.name}</div>
                <div className="text-sm text-gray-500">{user?.email}</div>
                <div className="text-sm text-gray-500">{user?.roles.join(', ')}</div>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to="/settings"
                className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center">
                  <Cog6ToothIcon className="w-5 h-5 mr-3" />
                  Einstellungen
                </div>
              </Link>
              {hasRole(['administrator', 'redakteur']) && (
                <Link
                  to="/admin"
                  className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    <CogIcon className="w-5 h-5 mr-3" />
                    Administration
                  </div>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 mr-3" />
                  Abmelden
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
